import { useEffect, useMemo, useState } from "react";
import { Activity, AlertTriangle, Bug, CheckCircle2, Copy, KeyRound, LogOut, Pause, Play, RefreshCw, ShieldCheck, StepForward, Store, XCircle } from "lucide-react";
import { toast } from "sonner";
import MacJitLogo from "../components/MacJitLogo";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const defaultConstraints = {
  transaction_count: 99,
  storage_percent: 100,
};

const endpointPresets = [
  {
    label: "Mini storage",
    method: "GET",
    path: "/ledger/storage",
    body: "",
    hint: "Actual request used by Mini storage meter. Requires tenant/QA token. No payload.",
    headers: { Authorization: "Bearer <terminal-token>", "X-Trace-Id": "qa-manual-id" },
    source: { module: "Mini", file: "backend/src/main/java/com/macjit/garage/controller/LedgerController.java", line: 171, component: "LedgerController.storageUsage" },
  },
  {
    label: "Mini create bill",
    method: "POST",
    path: "/ledger/bills",
    body: JSON.stringify({ title: "QA test bill", total: 1, items: [{ name: "QA item", qty: 1, price: 1 }], customer_name: "QA Customer", customer_phone: "+919999999999", customer_email: "qa.customer@macjit.test", payment_method: "cash", payment_channel: "counter", manual_lines: [] }, null, 2),
    hint: "Same shape as Mini Create bill. Change item name, qty, price, customer, payment_method, or payment_channel.",
    headers: { Authorization: "Bearer <terminal-token>", "Content-Type": "application/json", "X-Trace-Id": "qa-manual-id" },
    source: { module: "Mini", file: "backend/src/main/java/com/macjit/garage/controller/LedgerController.java", line: 73, component: "LedgerController.createBill" },
  },
  {
    label: "Create Mini merchant",
    method: "POST",
    path: "/qa/mini-merchant",
    body: JSON.stringify({ email: "mini.merchant@macjit.test", phone: "+919999999991", name: "QA Mini Merchant", shop_name: "QA Mini Counter", reset_data: true }, null, 2),
    hint: "Dev/local sandbox helper. Creates or resets a Mini merchant credential, trial subscription, tenant, and garage for QA testing.",
    headers: { Authorization: "Bearer <qa-or-platform-token>", "Content-Type": "application/json", "X-Trace-Id": "qa-manual-id" },
    source: { module: "QA", file: "backend/src/main/java/com/macjit/garage/controller/QaLabController.java", line: 118, component: "QaLabController.createOrResetMiniMerchant" },
  },
  {
    label: "Subscription me",
    method: "GET",
    path: "/admin/subscription/me",
    body: "",
    hint: "Actual subscription entitlement check used by terminal pages. No payload.",
    headers: { Authorization: "Bearer <terminal-token>", "X-Trace-Id": "qa-manual-id" },
    source: { module: "Subscription", file: "backend/src/main/java/com/macjit/garage/controller/AdminController.java", line: 286, component: "AdminController.subscription" },
  },
  {
    label: "Payment setup",
    method: "GET",
    path: "/payments/gateway-settings",
    body: "",
    hint: "Actual Mini payment setup fetch. It returns masked key details only.",
    headers: { Authorization: "Bearer <terminal-token>", "X-Trace-Id": "qa-manual-id" },
    source: { module: "Payments", file: "backend/src/main/java/com/macjit/garage/controller/PaymentOpsController.java", line: 26, component: "PaymentOpsController.gatewaySettings" },
  },
  {
    label: "Support conversation",
    method: "POST",
    path: "/public/support/conversations",
    body: JSON.stringify({ name: "QA Visitor", email: "qa.visitor@macjit.test", phone: "+919999999999", message: "QA support request" }, null, 2),
    hint: "Same shape as landing page product support chat first message. Public endpoint, no auth required.",
    headers: { "Content-Type": "application/json", "X-Trace-Id": "qa-manual-id" },
    source: { module: "Support", file: "backend/src/main/java/com/macjit/garage/controller/SupportController.java", line: 37, component: "SupportController.create" },
  },
];

export default function QaLabPage() {
  const { user, logout } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selected, setSelected] = useState("all_smoke");
  const [environment, setEnvironment] = useState("local");
  const [constraints, setConstraints] = useState(defaultConstraints);
  const [activeRun, setActiveRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [debugMode, setDebugMode] = useState(true);
  const [debugStep, setDebugStep] = useState(0);
  const [account, setAccount] = useState({ email: "", name: "MacJit QA", password: "" });
  const [createdAccount, setCreatedAccount] = useState(null);
  const [merchant, setMerchant] = useState({ email: "mini.merchant@macjit.test", phone: "+919999999991", name: "QA Mini Merchant", shop_name: "QA Mini Counter", password: "", reset_data: true });
  const [createdMerchant, setCreatedMerchant] = useState(null);
  const [composer, setComposer] = useState(endpointPresets[0]);

  const load = async () => {
    const [scenarioRes, runRes] = await Promise.all([
      api.get("/qa/scenarios"),
      api.get("/qa/runs"),
    ]);
    setScenarios(Array.isArray(scenarioRes.data) ? scenarioRes.data : []);
    const latest = Array.isArray(runRes.data) ? runRes.data : [];
    setRuns(latest);
    setActiveRun((current) => current || latest[0] || null);
  };

  useEffect(() => { load().catch(() => toast.error("Could not load QA Lab")); }, []);

  const scenario = useMemo(() => scenarios.find((item) => item.key === selected), [scenarios, selected]);
  const updateConstraint = (key, value) => setConstraints((current) => ({ ...current, [key]: Number(value || 0) }));
  const selectPreset = (label) => {
    const preset = endpointPresets.find((item) => item.label === label);
    if (preset) setComposer(preset);
  };

  const run = async () => {
    setBusy(true);
    try {
      const res = await api.post("/qa/run", { scenario: selected, environment, constraints, debug_mode: debugMode });
      setActiveRun(res.data);
      setDebugStep(0);
      toast.success(res.data?.passed ? "QA scenario passed" : "QA scenario failed");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "QA run failed");
    } finally {
      setBusy(false);
    }
  };

  const sendManualRequest = async () => {
    const traceId = `manual-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    let payload = undefined;
    try {
      payload = composer.body?.trim() ? JSON.parse(composer.body) : undefined;
    } catch {
      return toast.error("Payload must be valid JSON");
    }
    setBusy(true);
    const started = performance.now();
    try {
      const res = await api.request({
        method: composer.method.toLowerCase(),
        url: composer.path,
        data: ["POST", "PUT", "PATCH", "DELETE"].includes(composer.method) ? payload : undefined,
        headers: { "X-Trace-Id": traceId },
      });
      const ms = Math.round(performance.now() - started);
      setActiveRun(manualRun({ traceId, composer, payload, status: res.status, response: res.data, ms, passed: res.status < 400 }));
      setDebugStep(0);
      toast.success("Manual QA request completed");
    } catch (err) {
      const ms = Math.round(performance.now() - started);
      setActiveRun(manualRun({ traceId, composer, payload, status: err.response?.status || 0, response: err.response?.data || { message: err.message }, ms, passed: false }));
      setDebugStep(0);
      toast.error(err.response?.data?.detail || "Manual QA request failed");
    } finally {
      setBusy(false);
    }
  };

  const createAccount = async () => {
    if (!account.email.trim()) return toast.error("QA email is required");
    setBusy(true);
    try {
      const res = await api.post("/qa/account", account);
      setCreatedAccount(res.data);
      toast.success("QA credential ready for Terminal login");
      setAccount((current) => ({ ...current, password: "" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create QA account");
    } finally {
      setBusy(false);
    }
  };

  const createMiniMerchant = async () => {
    if (!merchant.email.trim()) return toast.error("Merchant email is required");
    setBusy(true);
    try {
      const res = await api.post("/qa/mini-merchant", merchant);
      setCreatedMerchant(res.data);
      toast.success("Mini merchant sandbox ready");
      setMerchant((current) => ({ ...current, password: "" }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create Mini merchant");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="sticky top-0 z-20 border-b border-zinc-800 bg-zinc-950/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-3">
            <MacJitLogo size={34} />
            <div>
              <p className="font-display text-lg font-black tracking-tighter">MACJIT <span className="text-orange-500">/ QA LAB</span></p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{user?.name || "Quality console"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load().catch(() => {})} className="border border-zinc-800 p-2 text-zinc-300 hover:border-orange-500 hover:text-orange-500" aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={logout} className="border border-zinc-800 p-2 text-zinc-300 hover:border-orange-500 hover:text-orange-500" aria-label="Logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl gap-6 px-6 py-8 xl:grid-cols-[380px_1fr]">
        <section className="space-y-5">
          {user?.role === "platform_admin" && (
            <div className="border border-zinc-800 bg-zinc-900/40 p-5">
              <div className="mb-4 flex items-center gap-3">
                <KeyRound className="h-5 w-5 text-orange-500" />
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">QA credential</p>
                  <h2 className="font-display text-xl font-semibold">Terminal access</h2>
                </div>
              </div>
              <div className="space-y-3">
                <Field label="Email" type="email" value={account.email} onChange={(v) => setAccount({ ...account, email: v.toLowerCase() })} />
                <Field label="Name" value={account.name} onChange={(v) => setAccount({ ...account, name: v })} />
                <Field label="Password optional" type="password" value={account.password} onChange={(v) => setAccount({ ...account, password: v })} />
                <button onClick={createAccount} disabled={busy} className="flex w-full items-center justify-center gap-2 bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
                  <ShieldCheck className="h-4 w-4" /> Create / reset
                </button>
                {createdAccount && (
                  <div className="border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-emerald-100">
                    <p className="font-mono">Login: {createdAccount.email}</p>
                    <p className="font-mono">Password: {createdAccount.temporary_password}</p>
                    <p className="mt-1 text-emerald-200">Use the normal Terminal login. It redirects to QA Lab.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="mb-4 flex items-center gap-3">
              <Store className="h-5 w-5 text-orange-500" />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Mini merchant</p>
                <h2 className="font-display text-xl font-semibold">Dev sandbox login</h2>
              </div>
            </div>
            <div className="space-y-3">
              <Field label="Merchant email" type="email" value={merchant.email} onChange={(v) => setMerchant({ ...merchant, email: v.toLowerCase() })} />
              <Field label="Mobile" value={merchant.phone} onChange={(v) => setMerchant({ ...merchant, phone: v })} />
              <Field label="Owner name" value={merchant.name} onChange={(v) => setMerchant({ ...merchant, name: v })} />
              <Field label="Shop name" value={merchant.shop_name} onChange={(v) => setMerchant({ ...merchant, shop_name: v })} />
              <Field label="Password optional" type="password" value={merchant.password} onChange={(v) => setMerchant({ ...merchant, password: v })} />
              <label className="flex items-center gap-2 border border-zinc-800 bg-black px-3 py-3 text-sm text-zinc-300">
                <input type="checkbox" checked={merchant.reset_data} onChange={(e) => setMerchant({ ...merchant, reset_data: e.target.checked })} />
                Reset Mini bills, credits, payment setup, and QA ledger data
              </label>
              <button onClick={createMiniMerchant} disabled={busy} className="flex w-full items-center justify-center gap-2 bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
                <ShieldCheck className="h-4 w-4" /> Create / reset Mini merchant
              </button>
              {createdMerchant && (
                <div className="border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs text-emerald-100">
                  <p className="font-mono">Login: {createdMerchant.merchant?.email}</p>
                  <p className="font-mono">Password: {createdMerchant.temporary_password}</p>
                  <p className="font-mono">Tenant: {createdMerchant.tenant_id}</p>
                  <p className="font-mono">Garage: {createdMerchant.garage_id}</p>
                  <p className="mt-1 text-emerald-200">Use normal Terminal login. Expected redirect: {createdMerchant.expected_redirect}</p>
                </div>
              )}
            </div>
          </div>

          <div className="border border-zinc-800 bg-zinc-900/40 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Scenario runner</p>
            <h1 className="mt-1 font-display text-2xl font-black tracking-tight">Click And Test</h1>
            <div className="mt-5 space-y-3">
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Environment</span>
                <select value={environment} onChange={(e) => setEnvironment(e.target.value)} className="mt-2 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm outline-none focus:border-orange-500">
                  <option value="local">Local</option>
                  <option value="dev">Dev</option>
                  <option value="staging">Staging</option>
                  <option value="prod-readonly">Prod readonly</option>
                </select>
              </label>
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Scenario</span>
                <select value={selected} onChange={(e) => setSelected(e.target.value)} className="mt-2 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm outline-none focus:border-orange-500">
                  {scenarios.map((item) => <option key={item.key} value={item.key}>{item.name}</option>)}
                </select>
              </label>
              {scenario && <p className="text-xs leading-relaxed text-zinc-500">{scenario.module} · {scenario.description}</p>}
              <label className="flex items-center gap-2 border border-zinc-800 bg-black px-3 py-3 text-sm text-zinc-300">
                <input type="checkbox" checked={debugMode} onChange={(e) => setDebugMode(e.target.checked)} />
                Debug stepper mode
              </label>
              <ConstraintSlider label="Mini transaction count" value={constraints.transaction_count} min={0} max={120} onChange={(v) => updateConstraint("transaction_count", v)} />
              <ConstraintSlider label="Storage pressure %" value={constraints.storage_percent} min={0} max={120} onChange={(v) => updateConstraint("storage_percent", v)} />
              <button onClick={run} disabled={busy} className="flex w-full items-center justify-center gap-2 bg-orange-500 py-4 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
                <Play className="h-4 w-4" /> {busy ? "Running..." : "Run test"}
              </button>
            </div>
          </div>

          <div className="border border-zinc-800 bg-zinc-900/40 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Request composer</p>
            <h2 className="mt-1 font-display text-xl font-semibold">Dummy data probe</h2>
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Preset</span>
                <select value={composer.label} onChange={(e) => selectPreset(e.target.value)} className="mt-2 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm outline-none focus:border-orange-500">
                  {endpointPresets.map((item) => <option key={item.label}>{item.label}</option>)}
                </select>
              </label>
              <div className="grid grid-cols-[110px_1fr] gap-2">
                <select value={composer.method} onChange={(e) => setComposer({ ...composer, method: e.target.value })} className="border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm outline-none focus:border-orange-500">
                  {["GET", "POST", "PUT", "PATCH", "DELETE"].map((method) => <option key={method}>{method}</option>)}
                </select>
                <input value={composer.path} onChange={(e) => setComposer({ ...composer, path: e.target.value })} className="border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm outline-none focus:border-orange-500" />
              </div>
              <div className="border border-zinc-800 bg-black p-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-orange-500">Exact request hint</p>
                <p className="mt-2 text-xs leading-relaxed text-zinc-400">{composer.hint || "Use the same path, method, headers, and JSON body as the real application request."}</p>
                <div className="mt-3 grid gap-2 text-xs">
                  <div className="min-w-0 rounded-sm bg-zinc-950 p-2 font-mono text-zinc-300">
                    <span className="text-zinc-500">Mapped endpoint:</span> <span className="break-all text-orange-300">{composer.source?.component || sourceForPath(composer.path).component}</span>
                  </div>
                  <div className="min-w-0 rounded-sm bg-zinc-950 p-2 font-mono text-zinc-300">
                    <span className="text-zinc-500">Source:</span> <span className="break-all">{composer.source?.file || sourceForPath(composer.path).file}:{composer.source?.line || sourceForPath(composer.path).line}</span>
                  </div>
                  <pre className="max-h-28 min-w-0 overflow-auto whitespace-pre-wrap break-words rounded-sm bg-zinc-950 p-2 font-mono text-zinc-400">{JSON.stringify(composer.headers || {}, null, 2)}</pre>
                </div>
              </div>
              <textarea value={composer.body} onChange={(e) => setComposer({ ...composer, body: e.target.value })} placeholder="JSON payload" className="min-h-40 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-xs outline-none focus:border-orange-500" />
              <button onClick={sendManualRequest} disabled={busy} className="flex w-full items-center justify-center gap-2 border border-orange-500 py-3 font-display font-black uppercase tracking-widest text-orange-300 hover:bg-orange-500 hover:text-black disabled:opacity-50">
                <Bug className="h-4 w-4" /> Send probe
              </button>
            </div>
          </div>

          <div className="border border-zinc-800 bg-black">
            <p className="border-b border-zinc-800 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Recent runs</p>
            <div className="max-h-96 overflow-y-auto">
              {runs.map((item) => (
                <button key={item.id || item.trace_id} onClick={() => setActiveRun(item)} className="block w-full border-b border-zinc-900 px-4 py-3 text-left hover:bg-zinc-900">
                  <p className={`font-display font-semibold ${item.passed ? "text-emerald-300" : "text-red-300"}`}>{item.scenario}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{item.status} · {item.environment} · {formatTs(item.created_at)}</p>
                </button>
              ))}
              {!runs.length && <p className="px-4 py-6 text-sm text-zinc-500">No QA runs yet.</p>}
            </div>
          </div>
        </section>

        <section className="min-w-0 space-y-5">
          <RunReport run={activeRun} debugMode={debugMode} debugStep={debugStep} setDebugStep={setDebugStep} />
        </section>
      </main>
    </div>
  );
}

function RunReport({ run, debugMode, debugStep, setDebugStep }) {
  if (!run) {
    return (
      <div className="grid min-h-[420px] place-items-center border border-zinc-800 bg-zinc-900/30 text-center">
        <div>
          <Activity className="mx-auto h-10 w-10 text-orange-500" />
          <p className="mt-4 font-display text-2xl font-black">Run a QA scenario</p>
          <p className="mt-2 text-sm text-zinc-500">The report will show trace id, steps, request shape, file, line, component, and stack details.</p>
        </div>
      </div>
    );
  }
  const steps = run.steps || [];
  const visibleSteps = debugMode ? steps.slice(0, debugStep + 1) : steps;
  const canNext = debugMode && debugStep < Math.max(0, steps.length - 1);
  return (
    <>
      <div className={`border p-5 ${run.passed ? "border-emerald-500/40 bg-emerald-500/10" : "border-red-500/40 bg-red-500/10"}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">QA report</p>
            <h2 className="mt-1 font-display text-3xl font-black">{run.scenario}</h2>
            <p className="mt-2 font-mono text-xs text-zinc-400">{run.trace_id}</p>
          </div>
          <span className={`inline-flex items-center gap-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-widest ${run.passed ? "border-emerald-500/50 text-emerald-300" : "border-red-500/50 text-red-300"}`}>
            {run.passed ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} {run.status}
          </span>
        </div>
        {debugMode && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-zinc-800 pt-4">
            <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-yellow-300"><Pause className="h-3.5 w-3.5" /> Debug paused at step {Math.min(debugStep + 1, steps.length)} / {steps.length}</span>
            <button disabled={!canNext} onClick={() => setDebugStep((value) => Math.min(value + 1, steps.length - 1))} className="inline-flex items-center gap-2 border border-yellow-500/60 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-yellow-300 hover:bg-yellow-500 hover:text-black disabled:opacity-40">
              <StepForward className="h-3.5 w-3.5" /> Next step
            </button>
            <button onClick={() => setDebugStep(steps.length - 1)} className="border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
              Run to end
            </button>
          </div>
        )}
      </div>
      <TraceConsole run={{ ...run, steps: visibleSteps }} />
      <div className="space-y-4">
        {visibleSteps.map((step, index) => <StepCard key={`${step.name}-${index}`} step={step} index={index} />)}
      </div>
    </>
  );
}

function TraceConsole({ run }) {
  const entries = (run.steps || []).flatMap((step, index) => traceEntries(step, index));
  return (
    <div className="border border-zinc-800 bg-black">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 px-4 py-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Visual trace console</p>
        <button onClick={() => navigator.clipboard?.writeText(JSON.stringify(run, null, 2))} className="inline-flex items-center gap-2 border border-zinc-700 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
          <Copy className="h-3.5 w-3.5" /> Copy report
        </button>
      </div>
      <div className="max-h-[420px] overflow-auto p-4 font-mono text-xs">
        {entries.map((entry, index) => (
          <div key={`${entry.label}-${index}`} className={`mb-2 border-l-2 px-3 py-2 ${levelClass(entry.level)}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="uppercase tracking-widest">{entry.level}</span>
              <span className="text-zinc-500">{entry.where}</span>
            </div>
            <p className="mt-1 text-zinc-200">{entry.label}</p>
            {entry.detail && <pre className="mt-2 max-h-36 overflow-auto whitespace-pre-wrap break-words text-zinc-400">{typeof entry.detail === "string" ? entry.detail : JSON.stringify(entry.detail, null, 2)}</pre>}
          </div>
        ))}
        {!entries.length && <p className="text-zinc-500">No trace entries yet.</p>}
      </div>
    </div>
  );
}

function StepCard({ step, index }) {
  const data = step.data || {};
  const source = step.source || {};
  const flow = inferFlow(step, index);
  return (
    <div className="border border-zinc-800 bg-zinc-900/40 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Step {index + 1} · {step.module}</p>
          <h3 className="mt-1 font-display text-xl font-semibold">{step.name}</h3>
          <p className="mt-1 text-sm text-zinc-500">{step.detail}</p>
        </div>
        <span className={`inline-flex items-center gap-2 border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${step.passed ? "border-emerald-500/50 text-emerald-300" : "border-red-500/50 text-red-300"}`}>
          {step.passed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />} {step.passed ? "Pass" : "Fail"}
        </span>
      </div>
      <div className="mt-4 grid min-w-0 gap-3 xl:grid-cols-2">
        <InfoBox title="Source">
          <p>{source.file || "-"}</p>
          <p>line {source.line || "-"} · {source.component || "-"}</p>
        </InfoBox>
        <InfoBox title="Mapped Endpoint">
          <p className="break-all">{data.backend_entry?.component || source.component || sourceForPath(String(data.request?.url || "").replace(/^\/api/, "")).component}</p>
          <p className="break-all">{data.backend_entry?.file || source.file || "-"}:{data.backend_entry?.line || source.line || "-"}</p>
        </InfoBox>
        <InfoBox title="Request">
          <pre className="qa-pre">{JSON.stringify(data.request || {}, null, 2)}</pre>
        </InfoBox>
        <InfoBox title="Payload / Response">
          <pre className="qa-pre">{JSON.stringify({ payload: data.request?.payload, response: data.response, duration_ms: data.duration_ms }, null, 2)}</pre>
        </InfoBox>
      </div>
      <div className="mt-4 grid gap-2 md:grid-cols-4">
        {flow.map((item) => (
          <div key={item.label} className={`border p-3 ${item.level === "error" ? "border-red-500/40 bg-red-500/10" : item.level === "warn" ? "border-yellow-500/40 bg-yellow-500/10" : "border-zinc-800 bg-black"}`}>
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{item.stage}</p>
            <p className="mt-1 text-sm font-semibold text-zinc-100">{item.label}</p>
            <p className="mt-1 font-mono text-[10px] text-zinc-500">{item.file}:{item.line}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function InfoBox({ title, children }) {
  return (
    <div className="min-w-0 overflow-hidden border border-zinc-800 bg-black p-4">
      <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">{title}</p>
      <div className="min-w-0 overflow-hidden font-mono text-xs leading-relaxed text-zinc-300">{children}</div>
    </div>
  );
}

function ConstraintSlider({ label, value, min, max, onChange }) {
  return (
    <label className="block">
      <span className="flex justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        <span>{label}</span><span>{value}</span>
      </span>
      <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(e.target.value)} className="mt-3 w-full accent-orange-500" />
    </label>
  );
}

function manualRun({ traceId, composer, payload, status, response, ms, passed }) {
  const source = sourceForPath(composer.path);
  const headers = { ...(composer.headers || {}), "X-Trace-Id": traceId };
  if (headers.Authorization) headers.Authorization = "***";
  return {
    trace_id: traceId,
    scenario: `manual ${composer.method} ${composer.path}`,
    environment: "local",
    status: passed ? "PASSED" : "FAILED",
    passed,
    created_at: new Date().toISOString(),
    steps: [{
      name: "Manual request probe",
      passed,
      module: source.module,
      detail: `Request completed with HTTP ${status} in ${ms}ms`,
      source: { file: "frontend/src/pages/QaLabPage.jsx", line: 58, component: "QaLabPage.sendManualRequest" },
      data: {
        request: {
          method: composer.method,
          url: `/api${composer.path}`,
          headers,
          payload,
        },
        response: { status, body: response },
        frontend_component: "QaLabPage",
        backend_entry: source,
        duration_ms: ms,
      },
    }],
  };
}

function sourceForPath(path) {
  const clean = String(path || "").replace(/^\/api/, "");
  const preset = endpointPresets.find((item) => clean.startsWith(item.path) || item.path.startsWith(clean));
  if (preset?.source) return preset.source;
  if (clean.startsWith("/ledger/storage")) return { module: "Mini", file: "backend/src/main/java/com/macjit/garage/controller/LedgerController.java", line: 171, component: "LedgerController.storageUsage" };
  if (clean.startsWith("/ledger/bills")) return { module: "Mini", file: "backend/src/main/java/com/macjit/garage/controller/LedgerController.java", line: 73, component: "LedgerController.createBill" };
  if (clean.startsWith("/qa/mini-merchant")) return { module: "QA", file: "backend/src/main/java/com/macjit/garage/controller/QaLabController.java", line: 118, component: "QaLabController.createOrResetMiniMerchant" };
  if (clean.startsWith("/admin/subscription")) return { module: "Subscription", file: "backend/src/main/java/com/macjit/garage/controller/AdminController.java", line: 286, component: "AdminController.subscription" };
  if (clean.startsWith("/payments/gateway-settings")) return { module: "Payments", file: "backend/src/main/java/com/macjit/garage/controller/PaymentOpsController.java", line: 26, component: "PaymentOpsController.gatewaySettings" };
  if (clean.startsWith("/public/support")) return { module: "Support", file: "backend/src/main/java/com/macjit/garage/controller/SupportController.java", line: 37, component: "SupportController.create" };
  return { module: "Unknown", file: "backend", line: "-", component: "Unmapped endpoint" };
}

function inferFlow(step, index) {
  const data = step.data || {};
  const request = data.request || {};
  const backend = data.backend_entry || sourceForPath(String(request.url || "").replace(/^\/api/, ""));
  const level = step.passed ? "info" : "error";
  return [
    { stage: "Frontend", label: "User action / QA trigger", file: "frontend/src/pages/QaLabPage.jsx", line: 42 + index, level },
    { stage: "API", label: `${request.method || "SCENARIO"} ${request.url || step.name}`, file: "frontend/src/lib/api.js", line: 16, level },
    { stage: "Backend", label: backend.component || step.source?.component || "Controller", file: backend.file || step.source?.file || "-", line: backend.line || step.source?.line || "-", level },
    { stage: "Result", label: step.passed ? "Completed" : "Failed", file: step.source?.file || "-", line: step.source?.line || "-", level },
  ];
}

function traceEntries(step, index) {
  const data = step.data || {};
  const request = data.request || {};
  const response = data.response || {};
  const level = step.passed ? "info" : "error";
  const entries = [
    { level: "info", where: `step ${index + 1}`, label: `Start: ${step.name}`, detail: step.detail },
    { level: request.method ? "info" : "warn", where: "frontend", label: request.method ? `${request.method} ${request.url}` : "No direct HTTP request captured for this synthetic scenario", detail: request },
    { level: "info", where: "backend", label: step.source?.component || "Backend source", detail: step.source },
    { level, where: "result", label: step.passed ? "PASS" : "FAIL", detail: response.body || data },
  ];
  if (data.stack) entries.push({ level: "error", where: "stack", label: "Exception stack", detail: data.stack });
  if (step.module === "Mini" && JSON.stringify(data).includes("100")) entries.push({ level: "warn", where: "constraint", label: "Constraint boundary reached", detail: "Storage/trial limit path should block writes and show archive/subscribe guidance." });
  return entries;
}

function levelClass(level) {
  if (level === "error") return "border-red-500 bg-red-500/10 text-red-300";
  if (level === "warn") return "border-yellow-500 bg-yellow-500/10 text-yellow-300";
  return "border-emerald-500/70 bg-emerald-500/5 text-emerald-300";
}

if (typeof document !== "undefined" && !document.getElementById("qa-lab-style")) {
  const style = document.createElement("style");
  style.id = "qa-lab-style";
  style.textContent = ".qa-pre{max-height:16rem;overflow:auto;white-space:pre-wrap;overflow-wrap:anywhere;word-break:break-word;max-width:100%;min-width:0;}";
  document.head.appendChild(style);
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full border border-zinc-800 bg-zinc-950 px-3 py-3 font-mono text-sm outline-none focus:border-orange-500" />
    </label>
  );
}

function formatTs(value) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value).slice(0, 19) : date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}
